import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useDashboardCurrency } from "@/hooks/useDashboardCurrency";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TableSkeleton } from "@/components/skeletons";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  ChevronRight,
  ChevronDown,
  Pause,
  Play,
  DollarSign,
  Pencil,
  Check,
  X,
  Loader2,
  ArrowUpDown,
  Settings2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import CampaignColumnChooser from "./campaign-column-chooser";
import type { CustomDashboardColumn } from "@shared/schema";
import { DEFAULT_CAMPAIGN_COLUMNS } from "@shared/schema";
import { managerCampaignsKey, managerAdSetsKey, managerAdsKey } from "@/lib/meta-query-keys";

// ─── Types ─────────────────────────────────

interface CampaignInsight {
  campaignId: string;
  campaignName: string;
  status: string;
  spend: string;
  impressions: string;
  clicks: string;
  cpc: string;
  cpm: string;
  ctr: string;
  reach: string;
  // PO: CPC/CTR + displayed clicks use outbound metrics. cpc/ctr above already
  // arrive unique-outbound from the backend; these counts drive display + totals.
  outboundClicks: string;
  uniqueOutboundClicks: string;
  leads: string;
  costPerLead: string;
  purchaseValue: string;
  purchaseRoas: string;
  schedules: string;
  costPerSchedule: string;
  dailyBudget?: number | null;
  lifetimeBudget?: number | null;
  actions?: { actionType: string; value: string }[];
}

interface AdSetInsight {
  adSetId: string;
  adSetName: string;
  campaignId: string;
  status: string;
  spend: string;
  impressions: string;
  clicks: string;
  cpc: string;
  cpm: string;
  ctr: string;
  reach: string;
  outboundClicks: string;
  uniqueOutboundClicks: string;
  leads: number;
  costPerLead: number;
  purchases?: string;
  purchaseValue?: string;
  purchaseRoas?: string;
  schedules: number;
  costPerSchedule: number;
}

interface AdInsight {
  adId: string;
  adName: string;
  adSetId: string;
  campaignId: string;
  spend: string;
  impressions: string;
  clicks: string;
  cpc: string;
  cpm: string;
  ctr: string;
  reach: string;
  outboundClicks: string;
  uniqueOutboundClicks: string;
  leads: number;
  costPerLead: number;
}

interface ClientCampaignTableProps {
  adAccountId: string;
  adAccountName: string;
  datePreset: string;
  customRange?: { since: string; until: string };
  readOnly?: boolean;
}

// ─── Formatters ────────────────────────────

function fmtCurrency(v: number, currency = "PLN"): string {
  const locale = currency === "USD" ? "en-US" : currency === "EUR" ? "de-DE" : "pl-PL";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(v);
}

function fmtNumber(v: number): string {
  return new Intl.NumberFormat("pl-PL").format(Math.round(v));
}

function fmtDecimal(v: number, digits = 2): string {
  return new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(v);
}

// ─── Sortable column header ────────────────

type SortKey = "name" | "leads" | "spend" | "cpl" | "cpc" | "cpm" | "ctr" | "roas" | "impressions";

function SortHeader({
  label,
  sortKey,
  currentKey,
  currentDir,
  onClick,
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  currentDir: "asc" | "desc";
  onClick: (key: SortKey) => void;
}) {
  const isActive = currentKey === sortKey;
  return (
    <button
      onClick={() => onClick(sortKey)}
      className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider hover:text-white transition-colors ${isActive ? "text-[#ef3e29]" : "text-white/40"}`}
    >
      {label}
      <ArrowUpDown className="w-3 h-3" />
    </button>
  );
}

// ─── Column value getter ───────────────────

const COLUMN_STORAGE_KEY = "gfy_campaign_columns";

function loadSavedColumns(): CustomDashboardColumn[] {
  try {
    const saved = localStorage.getItem(COLUMN_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    /* ignore */
  }
  return DEFAULT_CAMPAIGN_COLUMNS;
}

function saveColumns(cols: CustomDashboardColumn[]) {
  localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(cols));
}

function getColumnValue(campaign: CampaignInsight, key: string): number {
  const spend = parseFloat(campaign.spend) || 0;
  const leads = parseInt(campaign.leads) || 0;
  const impressions = parseInt(campaign.impressions) || 0;
  // Displayed click count is outbound (PO); cpc/ctr are already unique-outbound.
  const clicks = parseInt(campaign.outboundClicks) || 0;

  const calls = parseInt(campaign.schedules) || 0;
  const valueMap: Record<string, number> = {
    leads,
    spend,
    cpl: leads > 0 ? spend / leads : 0,
    cpc: parseFloat(campaign.cpc) || 0,
    cpm: parseFloat(campaign.cpm) || 0,
    ctr: parseFloat(campaign.ctr) || 0,
    roas: parseFloat(campaign.purchaseRoas) || 0,
    impressions,
    clicks,
    reach: parseInt(campaign.reach) || 0,
    frequency:
      impressions > 0 && parseInt(campaign.reach) > 0 ? impressions / parseInt(campaign.reach) : 0,
    purchase_value: parseFloat(campaign.purchaseValue) || 0,
    purchases: 0,
    cost_per_purchase: 0,
    aov: 0,
    calls,
    cost_per_call: calls > 0 ? spend / calls : parseFloat(campaign.costPerSchedule) || 0,
  };

  return valueMap[key] ?? 0;
}

function getAdSetColumnValue(adSet: AdSetInsight, key: string): number {
  const spend = parseFloat(adSet.spend) || 0;
  const impressions = parseInt(adSet.impressions) || 0;
  const reach = parseInt(adSet.reach) || 0;
  const outboundClicks = parseInt(adSet.outboundClicks) || parseInt(adSet.clicks) || 0;
  const calls = adSet.schedules || 0;
  const valueMap: Record<string, number> = {
    leads: adSet.leads,
    spend,
    cpl: adSet.costPerLead,
    cpc: parseFloat(adSet.cpc) || 0,
    cpm: parseFloat(adSet.cpm) || 0,
    ctr: parseFloat(adSet.ctr) || 0,
    roas: parseFloat(adSet.purchaseRoas || "0") || 0,
    impressions,
    clicks: outboundClicks,
    reach,
    frequency: impressions > 0 && reach > 0 ? impressions / reach : 0,
    purchases: parseFloat(adSet.purchases || "0") || 0,
    purchase_value: parseFloat(adSet.purchaseValue || "0") || 0,
    calls,
    cost_per_call: adSet.costPerSchedule || 0,
  };
  return valueMap[key] ?? 0;
}

function getAdColumnValue(ad: AdInsight, key: string): number {
  const spend = parseFloat(ad.spend) || 0;
  const impressions = parseInt(ad.impressions) || 0;
  const reach = parseInt(ad.reach) || 0;
  const outboundClicks = parseInt(ad.outboundClicks) || parseInt(ad.clicks) || 0;
  const valueMap: Record<string, number> = {
    leads: ad.leads,
    spend,
    cpl: ad.costPerLead,
    cpc: parseFloat(ad.cpc) || 0,
    cpm: parseFloat(ad.cpm) || 0,
    ctr: parseFloat(ad.ctr) || 0,
    roas: 0,
    impressions,
    clicks: outboundClicks,
    reach,
    frequency: impressions > 0 && reach > 0 ? impressions / reach : 0,
  };
  return valueMap[key] ?? 0;
}

function formatColumnValue(value: number, format?: string): string {
  if (value === 0 && format !== "number") return "—";

  const formatters: Record<string, () => string> = {
    currency: () => fmtCurrency(value),
    number: () => fmtNumber(value),
    percent: () => `${fmtDecimal(value)}%`,
    ratio: () => `${fmtDecimal(value)}x`,
  };

  return (formatters[format || "number"] || formatters.number)();
}

// ─── Main Component ────────────────────────

export default function ClientCampaignTable({
  adAccountId,
  adAccountName,
  datePreset,
  customRange,
  readOnly = false,
}: ClientCampaignTableProps) {
  const { t } = useTranslation();
  const dc = useDashboardCurrency();
  const fmtCurrency = (v: number) => dc.fmtShort(v);
  const [sortKey, setSortKey] = useState<SortKey>("spend");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [showColumnChooser, setShowColumnChooser] = useState(false);
  const [columns, setColumns] = useState<CustomDashboardColumn[]>(loadSavedColumns);
  const forcePreview = useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("preview") === "1";
  }, []);

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.visible).sort((a, b) => a.order - b.order),
    [columns]
  );

  const handleColumnsChange = (newCols: CustomDashboardColumn[]) => {
    setColumns(newCols);
    saveColumns(newCols);
  };

  const { data, isLoading, error } = useQuery<{
    campaigns: CampaignInsight[];
    preview?: { enabled: boolean; reason: string; source: string } | null;
  }>({
    queryKey: managerCampaignsKey(adAccountId, datePreset, customRange),
    queryFn: () =>
      apiRequest("POST", "/api/manager/client-campaigns", {
        adAccountId,
        datePreset,
        customRange,
        forcePreview: forcePreview || readOnly,
      }),
    staleTime: 60_000,
  });

  const campaigns = useMemo(() => data?.campaigns || [], [data?.campaigns]);
  const previewEnabled = data?.preview?.enabled === true;
  const effectiveReadOnly = readOnly || previewEnabled;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
      return;
    }
    setSortKey(key);
    setSortDir("desc");
  };

  const sortedCampaigns = useMemo(() => {
    const arr = [...campaigns];
    arr.sort((a, b) => {
      const valA = getSortValue(a, sortKey);
      const valB = getSortValue(b, sortKey);
      return sortDir === "desc" ? valB - valA : valA - valB;
    });
    return arr;
  }, [campaigns, sortKey, sortDir]);

  // Totals
  const totals = useMemo(() => {
    let spend = 0,
      leads = 0,
      impressions = 0,
      clicks = 0,
      reach = 0,
      uniqueOutbound = 0;
    for (const c of campaigns) {
      spend += parseFloat(c.spend) || 0;
      leads += parseInt(c.leads) || 0;
      impressions += parseInt(c.impressions) || 0;
      clicks += parseInt(c.outboundClicks) || 0;
      reach += parseInt(c.reach) || 0;
      uniqueOutbound += parseInt(c.uniqueOutboundClicks) || 0;
    }
    const cpl = leads > 0 ? spend / leads : 0;
    // PO: CPC = spend / unique outbound; CTR = unique outbound / reach.
    const cpc = uniqueOutbound > 0 ? spend / uniqueOutbound : 0;
    const cpm = impressions > 0 ? (spend / impressions) * 1000 : 0;
    const ctr = reach > 0 ? (uniqueOutbound / reach) * 100 : 0;
    return { spend, leads, impressions, clicks, cpl, cpc, cpm, ctr };
  }, [campaigns]);

  if (isLoading) {
    return (
      <div className="py-4 px-4">
        <TableSkeleton rows={4} columns={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-white/40 text-sm py-4 text-center">
        {t("clientCampaignTable.loadError")}
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="text-white/40 text-sm py-4 text-center">
        {t("clientCampaignTable.noCampaigns")}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.08]">
            <th className="text-left py-3 px-3 w-8" />
            <th className="text-left py-3 px-3 min-w-[200px]">
              <SortHeader
                label={t("clientCampaignTable.campaign")}
                sortKey="name"
                currentKey={sortKey}
                currentDir={sortDir}
                onClick={handleSort}
              />
            </th>
            {visibleColumns.map((col) => (
              <th key={col.key} className="text-right py-3 px-3">
                <SortHeader
                  label={col.label}
                  sortKey={col.key as SortKey}
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onClick={handleSort}
                />
              </th>
            ))}
            <th className="text-right py-3 px-3 min-w-[80px]">
              <div className="flex items-center gap-1 justify-end">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-white/40">
                  {effectiveReadOnly
                    ? t("clientCampaignTable.mode")
                    : t("clientCampaignTable.actions")}
                </span>
                <button
                  onClick={() => setShowColumnChooser(true)}
                  className="p-1 rounded hover:bg-white/[0.08] text-white/30 hover:text-white/60 transition-colors"
                  title={t("clientCampaignTable.configureColumns")}
                >
                  <Settings2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Totals row */}
          <tr className="border-b border-white/[0.08] bg-white/[0.03]">
            <td className="py-3 px-3" />
            <td className="py-3 px-3 text-white font-bold text-xs">
              {t("clientCampaignTable.total")}
            </td>
            {visibleColumns.map((col) => {
              const totalVal =
                col.key === "cpl"
                  ? totals.cpl
                  : col.key === "cpc"
                    ? totals.cpc
                    : col.key === "cpm"
                      ? totals.cpm
                      : col.key === "ctr"
                        ? totals.ctr
                        : col.key === "leads"
                          ? totals.leads
                          : col.key === "spend"
                            ? totals.spend
                            : col.key === "impressions"
                              ? totals.impressions
                              : col.key === "clicks"
                                ? totals.clicks
                                : 0;
              const isBold = col.key === "leads" || col.key === "spend" || col.key === "cpl";
              return (
                <td
                  key={col.key}
                  className={`py-3 px-3 text-right ${isBold ? "text-white font-bold" : "text-white/60"}`}
                >
                  {formatColumnValue(totalVal, col.format)}
                </td>
              );
            })}
            <td className="py-3 px-3" />
          </tr>

          {/* Campaign rows */}
          {sortedCampaigns.map((campaign) => (
            <CampaignRow
              key={campaign.campaignId}
              campaign={campaign}
              adAccountId={adAccountId}
              datePreset={datePreset}
              customRange={customRange}
              visibleColumns={visibleColumns}
              readOnly={effectiveReadOnly}
            />
          ))}
        </tbody>
      </table>

      {/* Column Chooser Dialog */}
      {showColumnChooser && (
        <CampaignColumnChooser
          columns={columns}
          onChange={handleColumnsChange}
          onClose={() => setShowColumnChooser(false)}
        />
      )}
    </div>
  );
}

// ─── Campaign Row (expandable) ─────────────

function CampaignRow({
  campaign,
  adAccountId,
  datePreset,
  customRange,
  visibleColumns,
  readOnly = false,
}: {
  campaign: CampaignInsight;
  adAccountId: string;
  datePreset: string;
  customRange?: { since: string; until: string };
  visibleColumns: CustomDashboardColumn[];
  readOnly?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const isInteractive = !readOnly;

  return (
    <>
      <tr
        className={`border-b border-white/[0.05] transition-colors ${isInteractive ? "hover:bg-white/[0.03] cursor-pointer" : ""}`}
        onClick={() => {
          if (isInteractive) setExpanded(!expanded);
        }}
      >
        <td className="py-2.5 px-3 text-white/40">
          {isInteractive ? (
            expanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )
          ) : (
            <span className="text-[10px] text-white/30">preview</span>
          )}
        </td>
        <td
          className="py-2.5 px-3 text-white text-xs font-medium truncate max-w-[250px]"
          title={campaign.campaignName}
        >
          {campaign.campaignName}
        </td>
        {visibleColumns.map((col) => (
          <td key={col.key} className="py-2.5 px-3 text-right text-white">
            {formatColumnValue(getColumnValue(campaign, col.key), col.format)}
          </td>
        ))}
        <td className="py-2.5 px-3 text-right" onClick={(e) => e.stopPropagation()}>
          {readOnly ? (
            <span className="text-[10px] uppercase tracking-wide text-white/30">Preview</span>
          ) : (
            <EntityActions
              entityType="campaign"
              entityId={campaign.campaignId}
              entityName={campaign.campaignName}
              adAccountId={adAccountId}
              currentBudget={campaign.dailyBudget}
              datePreset={datePreset}
            />
          )}
        </td>
      </tr>
      {expanded && !readOnly && (
        <tr>
          <td colSpan={visibleColumns.length + 3} className="p-0">
            <AdSetList
              campaignId={campaign.campaignId}
              adAccountId={adAccountId}
              datePreset={datePreset}
              customRange={customRange}
              visibleColumns={visibleColumns}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Ad Set List (loaded on expand) ────────

function AdSetList({
  campaignId,
  adAccountId,
  datePreset,
  customRange,
  visibleColumns,
}: {
  campaignId: string;
  adAccountId: string;
  datePreset: string;
  customRange?: { since: string; until: string };
  visibleColumns: CustomDashboardColumn[];
}) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useQuery<{ adSets: AdSetInsight[] }>({
    queryKey: managerAdSetsKey(campaignId, adAccountId, datePreset, customRange),
    queryFn: () =>
      apiRequest("POST", "/api/manager/client-adsets", {
        campaignId,
        adAccountId,
        datePreset,
        customRange,
      }),
    staleTime: 60_000,
  });

  const adSets = data?.adSets || [];

  if (isLoading) {
    return (
      <div className="py-4 pl-12 pr-4 space-y-2">
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-5/6" />
        <Skeleton className="h-3.5 w-2/3" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-3 pl-12 text-white/35 text-xs">
        {t("clientCampaignTable.refreshAdSets")}
      </div>
    );
  }

  if (adSets.length === 0) {
    return (
      <div className="py-3 pl-12 text-white/30 text-xs">{t("clientCampaignTable.noAdSets")}</div>
    );
  }

  return (
    <div className="bg-white/[0.02]">
      {adSets.map((adSet) => (
        <AdSetRow
          key={adSet.adSetId}
          adSet={adSet}
          adAccountId={adAccountId}
          datePreset={datePreset}
          customRange={customRange}
          visibleColumns={visibleColumns}
        />
      ))}
    </div>
  );
}

// ─── Ad Set Row (expandable) ───────────────

function AdSetRow({
  adSet,
  adAccountId,
  datePreset,
  customRange,
  visibleColumns,
}: {
  adSet: AdSetInsight;
  adAccountId: string;
  datePreset: string;
  customRange?: { since: string; until: string };
  visibleColumns: CustomDashboardColumn[];
}) {
  const [expanded, setExpanded] = useState(false);
  const spend = parseFloat(adSet.spend) || 0;

  return (
    <>
      <div
        className="flex items-center border-b border-white/[0.04] hover:bg-white/[0.03] cursor-pointer transition-colors pl-8"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="py-2 px-3 text-white/30 w-8 flex-shrink-0">
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </div>
        <div
          className="py-2 px-3 flex-1 min-w-0 text-white/80 text-xs truncate"
          title={adSet.adSetName}
        >
          ↳ {adSet.adSetName}
        </div>
        {visibleColumns.map((col) => {
          const val = getAdSetColumnValue(adSet, col.key);
          return (
            <div key={col.key} className="py-2 px-3 w-20 text-right text-white/70 text-xs">
              {formatColumnValue(val, col.format)}
            </div>
          );
        })}
        <div className="py-2 px-3 w-20 text-right" onClick={(e) => e.stopPropagation()}>
          <EntityActions
            entityType="adset"
            entityId={adSet.adSetId}
            entityName={adSet.adSetName}
            adAccountId={adAccountId}
            datePreset={datePreset}
          />
        </div>
      </div>
      {expanded && (
        <AdList
          adSetId={adSet.adSetId}
          adAccountId={adAccountId}
          datePreset={datePreset}
          customRange={customRange}
          visibleColumns={visibleColumns}
        />
      )}
    </>
  );
}

// ─── Ads List (loaded on expand) ───────────

function AdList({
  adSetId,
  adAccountId,
  datePreset,
  customRange,
  visibleColumns,
}: {
  adSetId: string;
  adAccountId: string;
  datePreset: string;
  customRange?: { since: string; until: string };
  visibleColumns: CustomDashboardColumn[];
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<{ ads: AdInsight[] }>({
    queryKey: managerAdsKey(adSetId, adAccountId, datePreset, customRange),
    queryFn: () =>
      apiRequest("POST", "/api/manager/client-ads", {
        adSetId,
        adAccountId,
        datePreset,
        customRange,
      }),
    staleTime: 60_000,
  });

  const ads = data?.ads || [];

  if (isLoading) {
    return (
      <div className="py-3 pl-20 pr-4 space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
      </div>
    );
  }

  if (ads.length === 0) {
    return <div className="py-2 pl-20 text-white/25 text-xs">{t("clientCampaignTable.noAds")}</div>;
  }

  return (
    <div className="bg-white/[0.01]">
      {ads.map((ad) => (
        <AdRow
          key={ad.adId}
          ad={ad}
          adAccountId={adAccountId}
          datePreset={datePreset}
          visibleColumns={visibleColumns}
        />
      ))}
    </div>
  );
}

// ─── Ad Row ────────────────────────────────

function AdRow({
  ad,
  adAccountId,
  datePreset,
  visibleColumns,
}: {
  ad: AdInsight;
  adAccountId: string;
  datePreset: string;
  visibleColumns: CustomDashboardColumn[];
}) {
  return (
    <div className="flex items-center border-b border-white/[0.03] hover:bg-white/[0.02] pl-16">
      <div className="py-2 px-3 w-8 flex-shrink-0" />
      <div className="py-2 px-3 flex-1 min-w-0 text-white/60 text-xs truncate" title={ad.adName}>
        ↳↳ {ad.adName}
      </div>
      {visibleColumns.map((col) => {
        const val = getAdColumnValue(ad, col.key);
        return (
          <div key={col.key} className="py-2 px-3 w-20 text-right text-white/40 text-xs">
            {formatColumnValue(val, col.format)}
          </div>
        );
      })}
      <div className="py-2 px-3 w-20 text-right" onClick={(e) => e.stopPropagation()}>
        <EntityActions
          entityType="ad"
          entityId={ad.adId}
          entityName={ad.adName}
          adAccountId={adAccountId}
          datePreset={datePreset}
        />
      </div>
    </div>
  );
}

// ─── Entity Management Actions ─────────────

function EntityActions({
  entityType,
  entityId,
  entityName,
  adAccountId,
  currentBudget,
  datePreset,
}: {
  entityType: "campaign" | "adset" | "ad";
  entityId: string;
  entityName: string;
  adAccountId: string;
  currentBudget?: number | null;
  datePreset: string;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const dc = useDashboardCurrency();
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetValue, setBudgetValue] = useState("");

  const statusMutation = useMutation({
    mutationFn: (newStatus: "ACTIVE" | "PAUSED") =>
      apiRequest("POST", "/api/manager/apply-action", {
        action: "update_status",
        entityType,
        entityId,
        entityName,
        newStatus,
        description: `${newStatus === "PAUSED" ? t("clientCampaignTable.actionPause") : t("clientCampaignTable.actionActivate")} ${entityType} "${entityName}"`,
        adAccountId,
      }),
    onSuccess: () => {
      toast({
        title: t("clientCampaignTable.statusUpdated"),
        description: t("clientCampaignTable.statusChanged"),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/manager/client-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/manager/accounts-overview"] });
    },
    onError: () => {
      toast({
        title: t("clientCampaignTable.tryAgain"),
        description: t("clientCampaignTable.tryAgainDesc"),
        variant: "destructive",
      });
    },
  });

  const budgetMutation = useMutation({
    mutationFn: (newBudget: number) =>
      apiRequest("POST", "/api/manager/apply-action", {
        action: "update_budget",
        entityType,
        entityId,
        entityName,
        newDailyBudget: newBudget,
        description: `${t("clientCampaignTable.changeBudgetDesc", { type: entityType, name: entityName, amount: newBudget, currency: dc.currency })}`,
        adAccountId,
      }),
    onSuccess: () => {
      toast({
        title: t("clientCampaignTable.budgetUpdated"),
        description: t("clientCampaignTable.budgetChanged"),
      });
      setEditingBudget(false);
      queryClient.invalidateQueries({ queryKey: ["/api/manager/client-campaigns"] });
    },
    onError: () => {
      toast({
        title: t("clientCampaignTable.tryAgain"),
        description: t("clientCampaignTable.tryAgainDesc"),
        variant: "destructive",
      });
    },
  });

  const handleBudgetSave = () => {
    const parsed = parseFloat(budgetValue.replace(",", "."));
    if (isNaN(parsed) || parsed <= 0) {
      toast({
        title: t("clientCampaignTable.tryAgain"),
        description: t("clientCampaignTable.invalidAmount"),
        variant: "destructive",
      });
      return;
    }
    budgetMutation.mutate(parsed);
  };

  const isLoading = statusMutation.isPending || budgetMutation.isPending;

  return (
    <div className="flex items-center gap-1 justify-end">
      {editingBudget ? (
        <div className="flex items-center gap-1">
          <Input
            value={budgetValue}
            onChange={(e) => setBudgetValue(e.target.value)}
            className="w-20 h-6 text-xs bg-white/[0.06] border-white/[0.15] text-white px-1.5"
            placeholder={dc.currency}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleBudgetSave();
              if (e.key === "Escape") setEditingBudget(false);
            }}
          />
          <button
            onClick={handleBudgetSave}
            disabled={budgetMutation.isPending}
            className="text-emerald-400 hover:text-emerald-300 p-0.5"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setEditingBudget(false)}
            className="text-white/30 hover:text-white/60 p-0.5"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <>
          {/* Budget edit - only for campaign and adset */}
          {entityType !== "ad" && (
            <button
              onClick={() => {
                setBudgetValue(currentBudget ? String(currentBudget) : "");
                setEditingBudget(true);
              }}
              className="text-white/30 hover:text-white/60 p-1 rounded transition-colors"
              title={t("clientCampaignTable.editBudget")}
            >
              <DollarSign className="w-3.5 h-3.5" />
            </button>
          )}
          {/* Pause */}
          <button
            onClick={() => statusMutation.mutate("PAUSED")}
            disabled={isLoading}
            className="text-white/30 hover:text-amber-400 p-1 rounded transition-colors"
            title={t("clientCampaignTable.pause")}
          >
            {isLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Pause className="w-3.5 h-3.5" />
            )}
          </button>
          {/* Play */}
          <button
            onClick={() => statusMutation.mutate("ACTIVE")}
            disabled={isLoading}
            className="text-white/30 hover:text-emerald-400 p-1 rounded transition-colors"
            title={t("clientCampaignTable.activate")}
          >
            <Play className="w-3.5 h-3.5 translate-x-[8%]" />
          </button>
        </>
      )}
    </div>
  );
}

// ─── Sort helper ───────────────────────────

function getSortValue(c: CampaignInsight, key: SortKey): number {
  if (key === "name") return 0;
  return getColumnValue(c, key);
}
